/* eslint-disable no-restricted-globals */
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { makeAutoObservable } from 'mobx';
import { DateTime, TimezoneType } from 'src_common/utils/time/time';

export type FirstDayOfWeekType = 'monday' | 'sunday';
export type WeekDaysLabelsType = 'Mo' | 'Tu' | 'We' | 'Th' | 'Fr' | 'Sa' | 'Su';

interface DayType {
    date: DateTime;
    disabled: boolean;
    highlighted: boolean;
}

export class CalendarState {
    public showCalendar: boolean = false;
    public weekDaysLabels: Array<WeekDaysLabelsType>;
    public isVisited: boolean = false;
    public displayedMonth: DateTime = DateTime.current();
    public typedDateString: string | null = null;

    public constructor(
        private formInputState: FormInputState<string, unknown>,
        private firstDayOfWeek: FirstDayOfWeekType,
        private timezone: TimezoneType | undefined,
        private isCampaignCreation: boolean
    ) {
        makeAutoObservable(this);
        if (this.firstDayOfWeek === 'monday') {
            this.weekDaysLabels = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
        } else {
            this.weekDaysLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
        }
        this.displayedMonth =
            this.formInputState.value === ''
                ? DateTime.current().setTime(0, 0, 0, 0)
                : DateTime.from(this.formInputState.value) ?? DateTime.current();
    }

    public get selectedDate(): DateTime | null {
        if (this.formInputState.value === '') {
            return null;
        }

        const date = DateTime.from(this.formInputState.value);

        if (date === null) {
            return null;
        }

        const offsetHour = this.getOffsetHour(date);
        return date.addHours(-offsetHour);
    }

    public get selectedDateString(): string {
        if (this.selectedDate === null) {
            return '';
        }
        return this.selectedDate.format('DD-MM-YYYY HH:mm');
    }

    public get selectedTimeString(): string {
        if (this.selectedDate === null) {
            return '--:--';
        }
        return this.selectedDate.format('HH:mm');
    }

    public selectDate = (date: DateTime): void => {
        let hours = this.selectedDate?.getHour() ?? 0;
        let minutes = this.selectedDate?.getMinute() ?? 0;
        const offsetHour = this.getOffsetHour(date);

        const currentDate = DateTime.current();

        if (
            this.isCampaignCreation &&
            this.selectedDate === null &&
            currentDate.getDayOfMonth() === date.getDayOfMonth() &&
            currentDate.getMonth() === date.getMonth() &&
            currentDate.getYear() === date.getYear()
        ) {
            hours = 23;
            minutes = 59;
        }

        const newDate = date.setTime(hours, minutes, 0, 0).addHours(offsetHour);
        this.formInputState.setValueOnChange(newDate.toISOString());
    };

    public selectTime = (value: string): void => {
        if (this.selectedDate === null) {
            return;
        }

        const isValid = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);

        if (isValid) {
            const hours = parseInt(value.split(':')[0] ?? '0');
            const minutes = parseInt(value.split(':')[1] ?? '0');

            const newDate = this.selectedDate.setTime(hours, minutes, 0, 0);
            const offsetHour = this.getOffsetHour(newDate);
            const dateString = newDate.addHours(offsetHour).toISOString();

            this.formInputState.setValueOnChange(dateString);
        }
    };

    public setTypedDateString = (value: string | null): void => {
        this.typedDateString = value;
    };

    public applyTypedDateString = (): void => {
        if (this.typedDateString === null || this.typedDateString === '') {
            return;
        }

        // Regex validates a date-time string in the format "DD-MM-YYYY HH:MM"
        // with days (01–31), months (01–12), hours (00–23), and minutes (00–59)
        // e.g. 22-05-2025 11:14
        const regex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4} (0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
        const isValid = regex.test(this.typedDateString);

        if (isValid) {
            const date = this.typedDateString.split(' ')[0];
            const time = this.typedDateString.split(' ')[1] ?? '00:00';

            const days = date?.split('-')[0] ?? '';
            const months = date?.split('-')[1] ?? '';
            const years = date?.split('-')[2] ?? '';

            const dateTime = DateTime.from(`${years}-${months}-${days}T00:00:00Z`) ?? DateTime.current();

            this.selectDate(dateTime);
            this.selectTime(time);
            this.displayedMonth = dateTime;
        }
        this.setTypedDateString(null);
    };

    public setShowCalendar = (value: boolean): void => {
        if (!value && this.isVisited) {
            this.formInputState.setAsVisited();
        }
        if (value) {
            this.isVisited = true;
        }
        this.showCalendar = value;
    };

    public nextMonth = (): void => {
        this.displayedMonth = this.displayedMonth.addMonths(1);
    };

    public prevMonth = (): void => {
        this.displayedMonth = this.displayedMonth.addMonths(-1);
    };

    private getOffsetHour = (date: DateTime): number => {
        if (this.timezone === undefined) {
            return 0;
        }

        const deviceTime = date;
        const configTime = date.tz(this.timezone);

        let deviceOffset: number;
        let configOffset: number;

        if (deviceTime.format().includes('Z')) {
            deviceOffset = 0;
        } else {
            deviceOffset = Number(deviceTime.format().slice(-6, -3));
        }

        if (configTime.format().includes('Z')) {
            configOffset = 0;
        } else {
            configOffset = Number(configTime.format().slice(-6, -3));
        }

        if (Number.isNaN(deviceOffset) || Number.isNaN(configOffset)) {
            throw Error(
                `adjust timezone error: deviceOffset: ${deviceOffset} and configOffset: ${configOffset} cannot be NaN`
            );
        }

        const offset = deviceOffset - configOffset;
        return offset;
    };

    private isDayHighligthed = (date: DateTime): boolean => {
        if (this.selectedDate === null) {
            return date.format('DD MMMM YYYY') === DateTime.current().format('DD MMMM YYYY');
        }
        return date.format('DD MMMM YYYY') === this.selectedDate.format('DD MMMM YYYY');
    };

    public get daysInMonth(): Array<DayType> {
        const start = this.displayedMonth.startOfMonths();
        const end = this.displayedMonth.endOfMonths();
        let days: Array<{ date: DateTime; disabled: boolean }> = [];

        const totalDays: Array<DateTime> = Array.from({ length: end.getDayOfMonth() }, (_, index) =>
            start.addDays(index)
        );
        const startDay = (start.getDayOfWeek() + (this.firstDayOfWeek === 'monday' ? 6 : 0)) % 7;

        for (let i = 0; i < startDay; i++) {
            days.push({
                date: start.addDays(i - startDay),
                disabled: true,
            });
        }

        for (const day of totalDays) {
            days.push({
                date: day,
                disabled: false,
            });
        }

        const totalRenderedDays = days.length;
        const totalDaysOnPage = 42; // number of days in week * number of max rows on calendar page = 42
        const remainingDays = totalDaysOnPage - totalRenderedDays;

        for (let i = 0; i < remainingDays; i++) {
            days.push({
                date: end.addDays(i + 1),
                disabled: true,
            });
        }

        const lastRowStartIndex = days.length - 7;
        const lastRow = days.slice(lastRowStartIndex);

        const containsCurrentMonthDay = lastRow.some((day) => !day.disabled);

        if (!containsCurrentMonthDay) {
            days = days.slice(0, lastRowStartIndex);
        }

        const daysExt: Array<DayType> = days.map((day) => ({
            ...day,
            highlighted: this.isDayHighligthed(day.date),
        }));

        return daysExt;
    }
}
